import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import settingService from 'services/setting.service';

interface PrintSettings {
    AllowPrintTicketWhenSell: boolean;
    AllowPrintTicketWhenUse: boolean;
}

interface PrintSettingsContextType {
    printSettings: PrintSettings;
    refreshPrintSettings: () => Promise<void>;
    updatePrintSettings: (settings: any[]) => void;
}

const PrintSettingsContext = createContext<PrintSettingsContextType | undefined>(undefined);

export const usePrintSettings = () => {
    const context = useContext(PrintSettingsContext);
    if (!context) {
        throw new Error('usePrintSettings must be used within a PrintSettingsProvider');
    }
    return context;
};

interface PrintSettingsProviderProps {
    children: ReactNode;
}

export const PrintSettingsProvider: React.FC<PrintSettingsProviderProps> = ({ children }) => {
    const [printSettings, setPrintSettings] = useState<PrintSettings>({
        AllowPrintTicketWhenSell: false,
        AllowPrintTicketWhenUse: false
    });

    const refreshPrintSettings = async () => {
        try {
            const response: any = await settingService.getSettingPrint();
            if (response?.result) {
                const settings: PrintSettings = {
                    AllowPrintTicketWhenSell: false,
                    AllowPrintTicketWhenUse: false
                };

                response.result.forEach((setting: any) => {
                    if (setting.key === 'AllowPrintTicketWhenSell') {
                        settings.AllowPrintTicketWhenSell = setting.value === '1';
                    } else if (setting.key === 'AllowPrintTicketWhenUse') {
                        settings.AllowPrintTicketWhenUse = setting.value === '1';
                    }
                });

                setPrintSettings(settings);
            }
        } catch (error) {
            console.error('Failed to load print settings:', error);
        }
    };

    const updatePrintSettings = (settingsArray: any[]) => {
        const settings: PrintSettings = {
            AllowPrintTicketWhenSell: false,
            AllowPrintTicketWhenUse: false
        };

        settingsArray.forEach((setting: any) => {
            if (setting.key === 'AllowPrintTicketWhenSell') {
                settings.AllowPrintTicketWhenSell = setting.value === '1';
            } else if (setting.key === 'AllowPrintTicketWhenUse') {
                settings.AllowPrintTicketWhenUse = setting.value === '1';
            }
        });

        setPrintSettings(settings);
    };

    useEffect(() => {
        refreshPrintSettings();
    }, []);

    return (
        <PrintSettingsContext.Provider value={{ printSettings, refreshPrintSettings, updatePrintSettings }}>
            {children}
        </PrintSettingsContext.Provider>
    );
};
